import { useState, useRef } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Label } from '@/components/ui'
import { Image, Upload, Download, Trash2, ZoomOut } from 'lucide-react'
import { toast } from 'sonner'

interface ImageFile {
  id: string
  file: File
  preview: string
  originalSize: number
  compressedSize: number | null
  compressedUrl: string | null
}

export function ImageCompressor() {
  const [images, setImages] = useState<ImageFile[]>([])
  const [quality, setQuality] = useState(80)
  const [maxWidth, setMaxWidth] = useState(1920)
  const [processing, setProcessing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    const newImages: ImageFile[] = []

    Array.from(files).forEach((file) => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`)
        return
      }

      const preview = URL.createObjectURL(file)
      newImages.push({
        id: crypto.randomUUID(),
        file,
        preview,
        originalSize: file.size,
        compressedSize: null,
        compressedUrl: null,
      })
    })

    setImages((prev) => [...prev, ...newImages])
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const compressImage = async (imageFile: ImageFile): Promise<ImageFile> => {
    return new Promise((resolve) => {
      const img = new window.Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let width = img.width
        let height = img.height

        // Resize if needed
        if (width > maxWidth) {
          height = (height * maxWidth) / width
          width = maxWidth
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          resolve(imageFile)
          return
        }

        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(imageFile)
              return
            }

            const compressedUrl = URL.createObjectURL(blob)
            resolve({
              ...imageFile,
              compressedSize: blob.size,
              compressedUrl,
            })
          },
          'image/jpeg',
          quality / 100
        )
      }
      img.src = imageFile.preview
    })
  }

  const compressAll = async () => {
    if (images.length === 0) {
      toast.error('No images to compress')
      return
    }

    setProcessing(true)

    const compressed = await Promise.all(
      images.map((img) => compressImage(img))
    )

    setImages(compressed)
    setProcessing(false)
    toast.success('All images compressed!')
  }

  const downloadImage = (image: ImageFile) => {
    const url = image.compressedUrl || image.preview
    const link = document.createElement('a')
    link.href = url
    link.download = `compressed-${image.file.name}`
    link.click()
    toast.success('Image downloaded')
  }

  const downloadAll = () => {
    images.forEach((image) => {
      if (image.compressedUrl) {
        downloadImage(image)
      }
    })
  }

  const removeImage = (id: string) => {
    setImages((prev) => {
      const image = prev.find((img) => img.id === id)
      if (image) {
        URL.revokeObjectURL(image.preview)
        if (image.compressedUrl) {
          URL.revokeObjectURL(image.compressedUrl)
        }
      }
      return prev.filter((img) => img.id !== id)
    })
  }

  const clearAll = () => {
    images.forEach((image) => {
      URL.revokeObjectURL(image.preview)
      if (image.compressedUrl) {
        URL.revokeObjectURL(image.compressedUrl)
      }
    })
    setImages([])
  }

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  const getTotalSavings = (): { original: number; compressed: number; percent: number } => {
    const original = images.reduce((sum, img) => sum + img.originalSize, 0)
    const compressed = images.reduce((sum, img) => sum + (img.compressedSize || img.originalSize), 0)
    const percent = original > 0 ? ((original - compressed) / original) * 100 : 0
    return { original, compressed, percent }
  }

  const savings = getTotalSavings()

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Image className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Image Compressor</h1>
            <p className="text-muted-foreground">Reduce image file sizes while maintaining quality</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[350px_1fr]">
        {/* Settings */}
        <div className="space-y-6">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Settings</CardTitle>
              <CardDescription>Configure compression options</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Quality slider */}
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label>Quality</Label>
                  <span className="text-sm font-mono text-primary">{quality}%</span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={quality}
                  onChange={(e) => setQuality(Number(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Smaller file</span>
                  <span>Better quality</span>
                </div>
              </div>

              {/* Max width */}
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label>Max Width</Label>
                  <span className="text-sm font-mono text-primary">{maxWidth}px</span>
                </div>
                <input
                  type="range"
                  min="320"
                  max="4096"
                  step="64"
                  value={maxWidth}
                  onChange={(e) => setMaxWidth(Number(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
              </div>

              {/* Upload area */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
              >
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">Click to upload images</p>
                <p className="text-xs text-muted-foreground mt-1">PNG, JPG, WebP supported</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <Button
                  onClick={compressAll}
                  disabled={images.length === 0 || processing}
                  className="w-full"
                >
                  {processing ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Compressing...
                    </span>
                  ) : (
                    <>
                      <ZoomOut className="h-4 w-4 mr-2" />
                      Compress All
                    </>
                  )}
                </Button>
                <Button
                  onClick={downloadAll}
                  disabled={!images.some((img) => img.compressedUrl)}
                  variant="outline"
                  className="w-full"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download All
                </Button>
                <Button
                  onClick={clearAll}
                  disabled={images.length === 0}
                  variant="outline"
                  className="w-full"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear All
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          {images.length > 0 && (
            <Card className="glass">
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-foreground">{images.length}</p>
                    <p className="text-xs text-muted-foreground">Images</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{formatSize(savings.original)}</p>
                    <p className="text-xs text-muted-foreground">Original</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">{savings.percent.toFixed(0)}%</p>
                    <p className="text-xs text-muted-foreground">Saved</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Image Grid */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>Images</CardTitle>
            <CardDescription>Your uploaded images</CardDescription>
          </CardHeader>
          <CardContent>
            {images.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No images uploaded</p>
                <p className="text-xs mt-1">Upload images to get started</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {images.map((image) => (
                  <div
                    key={image.id}
                    className="border border-border rounded-lg overflow-hidden bg-muted/20"
                  >
                    <div className="aspect-video relative">
                      <img
                        src={image.compressedUrl || image.preview}
                        alt={image.file.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-3 space-y-2">
                      <p className="text-sm font-medium truncate">{image.file.name}</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">
                          {formatSize(image.originalSize)}
                        </span>
                        {image.compressedSize && (
                          <>
                            <span className="text-muted-foreground">→</span>
                            <span className="text-primary font-medium">
                              {formatSize(image.compressedSize)}
                            </span>
                            <span className="text-green-500">
                              -{(((image.originalSize - image.compressedSize) / image.originalSize) * 100).toFixed(0)}%
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => downloadImage(image)}
                          disabled={!image.compressedUrl}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeImage(image.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
